import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Checkbox, Box as Flex, Tooltip, styled } from '@material-ui/core';

import { RiDeleteBinLine, RiListCheck2, RiRefreshLine } from '@remixicon/react';
import type { Editor } from '@tiptap/core';
import { type NodeViewProps } from '@tiptap/react';

import { type AvailableMetric } from '@spotify/confidence-editor-react';
import { BlockReorderButtons } from '@spotify/confidence-editor-react';
import { BlockCommentButton } from '@spotify/confidence-editor-react';
import { ToolbarButton, ToolbarCard } from '@spotify/confidence-editor-react';
import { closeAllDropdowns } from '@spotify/confidence-editor-react';

import { createMetricRow } from './helpers';

const MenuButtonContainer = styled('div')({
  position: 'relative',
});

const MenuContainer = styled('div')({
  position: 'absolute',
  top: '100%',
  right: 0,
  marginTop: 'var(--bui-space-1)',
  minWidth: '300px',
  maxHeight: '400px',
  overflow: 'auto',
  backgroundColor: 'var(--bui-bg-surface-1)',
  border: '1px solid var(--bui-border)',
  borderRadius: 'var(--bui-radius-2)',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  zIndex: 1001,
});

const MetricOption = styled('div')({
  padding: 'var(--bui-space-2)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  minHeight: '40px',
  '&:hover': {
    backgroundColor: 'var(--bui-bg-surface-2)',
  },
});

type MetricRow = {
  metricId: string;
  name: string;
  type: 'guardrail' | 'success';
  value: string;
  sampleSize: string;
  lowerBound: string;
  upperBound: string;
  outcome: string;
  status?: string;
  hasNoninferiorityMargin?: boolean;
};

type MetricTableToolbarProps = {
  node: NodeViewProps['node'];
  deleteNode: NodeViewProps['deleteNode'];
  updateAttributes: NodeViewProps['updateAttributes'];
  metrics?: AvailableMetric[];
  editor: Editor;
  getPos?: () => number | undefined;
  isStale?: boolean;
  onRefresh?: () => void;
};

export const MetricTableToolbar = ({
  node,
  deleteNode,
  updateAttributes,
  metrics = [],
  editor,
  getPos,
  isStale,
  onRefresh,
}: MetricTableToolbarProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const rows: MetricRow[] = useMemo(
    () => node.attrs.rows || [],
    [node.attrs.rows],
  );

  const createRow = useCallback(
    (metric: AvailableMetric) => createMetricRow(metric),
    [],
  );

  const selectedMetricIds = useMemo(
    () => new Set(rows.map(r => r.metricId)),
    [rows],
  );

  const handleToggleMetric = useCallback(
    (metricId: string) => {
      const newSelectedIds = new Set(selectedMetricIds);

      if (newSelectedIds.has(metricId)) {
        if (newSelectedIds.size <= 1) {
          return;
        }
        newSelectedIds.delete(metricId);
      } else {
        newSelectedIds.add(metricId);
      }

      const newRows = metrics
        .filter(m => newSelectedIds.has(m.id))
        .map(createRow);

      updateAttributes({ rows: newRows });
    },
    [selectedMetricIds, metrics, updateAttributes, createRow],
  );

  const handleDelete = useCallback(() => {
    deleteNode();
  }, [deleteNode]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const handleMenuToggle = () => {
    if (!isMenuOpen) {
      closeAllDropdowns();
      const { to } = editor.state.selection;
      editor.commands.setTextSelection(to);
    }
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <ToolbarCard>
      <Flex
        display="flex"
        alignItems="center"
        style={{
          gap: 2,
        }}
      >
        <BlockReorderButtons getPos={getPos} editor={editor} />
        <MenuButtonContainer ref={menuRef}>
          <Tooltip title="Select metrics">
            <ToolbarButton
              aria-label="Select metrics"
              onClick={handleMenuToggle}
            >
              <RiListCheck2 />
            </ToolbarButton>
          </Tooltip>
          {isMenuOpen && (
            <MenuContainer>
              {metrics.length === 0 ? (
                <MetricOption style={{ cursor: 'default' }}>
                  No metrics available
                </MetricOption>
              ) : (
                metrics.map(metric => {
                  const isSelected = selectedMetricIds.has(metric.id);
                  const isLastSelected =
                    isSelected && selectedMetricIds.size === 1;

                  return (
                    <MetricOption key={metric.id}>
                      <Checkbox
                        checked={isSelected}
                        disabled={isLastSelected}
                        onChange={() => handleToggleMetric(metric.id)}
                      />
                      {metric.name}
                    </MetricOption>
                  );
                })
              )}
            </MenuContainer>
          )}
        </MenuButtonContainer>
        {isStale && (
          <Tooltip title="Update with latest metric values">
            <ToolbarButton
              aria-label="Update with latest metric values"
              onClick={onRefresh}
            >
              <RiRefreshLine color="var(--bui-fg-warning)" />
            </ToolbarButton>
          </Tooltip>
        )}
        <BlockCommentButton />
        <Tooltip title="Delete table">
          <ToolbarButton aria-label="Delete table" onClick={handleDelete}>
            <RiDeleteBinLine />
          </ToolbarButton>
        </Tooltip>
      </Flex>
    </ToolbarCard>
  );
};
